import React, { useMemo, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import { LambdaHistoryItem } from '@api/lambdaOperationsApi';
import { canOpenLambdaDashboard } from '../lambdaOperations.utils';

type Props = {
    item: LambdaHistoryItem;
    restarting: boolean;
    onOpenDashboard: (item: LambdaHistoryItem) => void;
    onRestart: (item: LambdaHistoryItem) => void;
};

const LambdaHistoryContextMenu = ({
    item,
    restarting,
    onOpenDashboard,
    onRestart,
}: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const { t } = useTranslation();

    const actions = useMemo(
        () => [
            ...(canOpenLambdaDashboard(item)
                ? [
                      {
                          label: t('lambda-operations.open-dashboard'),
                          icon: <OpenInNewIcon fontSize="small" />,
                          onClick: () => onOpenDashboard(item),
                          disabled: false,
                      },
                  ]
                : []),
            ...(item.isRestartable
                ? [
                      {
                          label: t('lambda-operations.restart'),
                          icon: <RestartAltIcon fontSize="small" />,
                          onClick: () => onRestart(item),
                          disabled: restarting,
                      },
                  ]
                : []),
        ],
        [item, onOpenDashboard, onRestart, restarting, t]
    );

    if (!actions.length) {
        return null;
    }

    return (
        <>
            <button
                type="button"
                className="lambda-history-menu-btn"
                onClick={event => setAnchorEl(event.currentTarget)}
                aria-label={t('lambda-operations.actions')}
                data-testid="lambdaHistoryMenuBtn"
            >
                <MoreVertIcon fontSize="small" />
            </button>
            <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
                {actions.map(action => (
                    <MenuItem
                        key={action.label}
                        disabled={action.disabled}
                        onClick={() => {
                            setAnchorEl(null);
                            action.onClick();
                        }}
                    >
                        <span className="lambda-history-menu-item">
                            {action.icon}
                            {action.label}
                        </span>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default LambdaHistoryContextMenu;
